import { LitElement, css, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { makeCustomEvent, EVENT } from '../../../util/events';
import { consume } from '@lit/context';
import { formDataContext } from '../../../contexts/formData';
import {
    isNonEmptyString,
    isValidEmail,
    isValidPhoneNumber,
    isValidName,
    MAX_DEPENDENTS,
    MAX_MEMBERS,
    RateQuoteContact,
    RateQuoteFormData,
    RateQuoteMember,
    RELATION,
} from '../../../util/form';
import '../member-card';
import '../../../components/continue-button';
import '../../../components/back-button';
import { isValidZipcode } from '../../../util/zipcode';
import { isUnder26Years, isValidBirthday, isUnder18Years } from '../../../util/date';
import { sourceContext } from '../../../contexts/source';
import { SOURCE } from '../../../util/source';
import { breakpointContext } from '../../../contexts/breakpoint';
import { BREAKPOINT } from '../../../util/breakpoints';

@customElement('rq-step-members')
export class RateQuoteMemberStep extends LitElement {
    static styles = css`
        .wrapper {
            display: flex;
            flex-direction: column;
            gap: 24px;
        }
        .contact-info {
            display: flex;
            flex-direction: column;
            gap: 12px;
        }
    `;

    connectedCallback() {
        super.connectedCallback();
        this.addEventListener(EVENT.REMOVE_MEMBER, this.removeMember as EventListener);
        this.addEventListener('lh-change', this.listenerHandler as EventListener);
        this.addEventListener('lh-input', this.listenerHandler as EventListener);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener(EVENT.REMOVE_MEMBER, this.removeMember as EventListener);
        this.removeEventListener('lh-change', this.listenerHandler as EventListener);
        this.removeEventListener('lh-input', this.listenerHandler as EventListener);
    }

    @consume({ context: formDataContext, subscribe: true })
    data!: RateQuoteFormData;

    @consume({ context: breakpointContext, subscribe: true })
    breakpoint!: BREAKPOINT;

    @consume({ context: sourceContext })
    source!: SOURCE;

    get isMobile() {
        return this.breakpoint === BREAKPOINT.PHONE || this.breakpoint === BREAKPOINT.TABLET;
    }

    private listenerHandler({
        detail: { name, value },
    }: CustomEvent<{ name: `contactInfo.${keyof RateQuoteContact}`; value: string }>) {
        const prop = name.split('.')[1] as keyof RateQuoteContact;
        if (!prop) return;

        const contactInfo: RateQuoteContact = this.data.contactInfo
            ? { ...this.data.contactInfo, [prop]: value }
            : {
                  firstName: '',
                  lastName: '',
                  phone: '',
                  email: '',
                  [prop]: value,
              };
        const updatedForm: RateQuoteFormData = {
            ...this.data,
            contactInfo,
        };
        this.dispatchEvent(makeCustomEvent(EVENT.UPDATE_FORM, { data: updatedForm }));
    }

    private onAddAnotherPerson() {
        const members = [...this.data.members];
        const spouseExists = this.data.members.some((m) => (m as RateQuoteMember).relation === RELATION.SPOUSE);
        const newMember: RateQuoteMember = { dob: '', relation: spouseExists ? RELATION.DEPENDENT : undefined };
        members.push(newMember);
        const updatedForm: RateQuoteFormData = {
            ...this.data,
            members,
        };
        this.dispatchEvent(makeCustomEvent(EVENT.UPDATE_FORM, { data: updatedForm }));
    }

    private removeMember(e: CustomEvent) {
        if (!e.detail.member) return;
        const idx = e.detail.member;
        const members = [...this.data.members];
        members.splice(idx, 1);
        const updatedForm: RateQuoteFormData = { ...this.data, members };
        this.dispatchEvent(makeCustomEvent(EVENT.UPDATE_FORM, { data: updatedForm }));
    }

    get disableContinue() {
        const primaryMember = this.data.members[0];
        const otherMembers = this.data.members.slice(1);
        return (
            primaryMember.state?.toLowerCase() !== 'nc' ||
            !isValidZipcode(primaryMember.zipcode) ||
            !isValidBirthday(primaryMember.dob) ||
            otherMembers.some(
                (m) =>
                    !m.dob ||
                    !isValidBirthday(m.dob) ||
                    !m.relation ||
                    (m.relation === RELATION.DEPENDENT && !isUnder26Years(m.dob))
            ) ||
            otherMembers.filter((m) => m.relation === RELATION.SPOUSE).length > 1 ||
            otherMembers.filter((m) => m.relation === RELATION.DEPENDENT).length > MAX_DEPENDENTS ||
            this.hasPrimaryMinorWithDependents ||
            (this.isEmbedded &&
                (!isNonEmptyString(this.data.contactInfo?.firstName) ||
                    !isValidName(this.data.contactInfo?.firstName) ||
                    !isNonEmptyString(this.data.contactInfo?.lastName) ||
                    !isValidName(this.data.contactInfo?.lastName) ||
                    !isValidPhoneNumber(this.data.contactInfo?.phone) ||
                    !isValidEmail(this.data.contactInfo?.email)))
        );
    }

    get hasPrimaryMinorWithDependents() {
        const primaryMember = this.data.members[0];
        const otherMembers = this.data.members.slice(1);
        return (
            primaryMember.dob &&
            isValidBirthday(primaryMember.dob) &&
            isUnder18Years(primaryMember.dob) &&
            otherMembers.length > 0
        );
    }

    get minorWithDependentsWarning() {
        if (!this.hasPrimaryMinorWithDependents) return nothing;
        return html`
            <lh-alert variant="error" inline>
                Applicants under the age of 18 may not have dependents. If you are applying for only minors, please fill
                out a separate application for each one.
            </lh-alert>
        `;
    }

    get isEmbedded() {
        return Boolean(this.source === SOURCE.EMBEDDED_QUOTE || this.data.pnumber);
    }

    get contactInfo() {
        if (!this.isEmbedded) return nothing;

        const firstNameValue = this.data.contactInfo?.firstName || '';
        const lastNameValue = this.data.contactInfo?.lastName || '';

        const firstNameError =
            firstNameValue && !isValidName(firstNameValue)
                ? 'Please enter a valid first name (letters, spaces, hyphens, apostrophes, and periods only)'
                : nothing;

        const lastNameError =
            lastNameValue && !isValidName(lastNameValue)
                ? 'Please enter a valid last name (letters, spaces, hyphens, apostrophes, and periods only)'
                : nothing;

        return html`
            <div class="contact-info">
                <lh-typography type="heading" selector="h3" size="lg"> Applicant contact information </lh-typography>
                <lh-input
                    name="contactInfo.firstName"
                    value=${firstNameValue}
                    default-value=${firstNameValue}
                    required
                    error-message=${firstNameError}
                    >First name</lh-input
                >
                <lh-input
                    name="contactInfo.lastName"
                    value=${lastNameValue}
                    default-value=${lastNameValue}
                    required
                    error-message=${lastNameError}
                    >Last name</lh-input
                >
                <lh-input
                    name="contactInfo.phone"
                    value=${ifDefined(this.data.contactInfo?.phone)}
                    default-value=${ifDefined(this.data.contactInfo?.phone)}
                    type="tel"
                    required
                    >Phone number</lh-input
                >
                <lh-input
                    name="contactInfo.email"
                    value=${ifDefined(this.data.contactInfo?.email)}
                    default-value=${ifDefined(this.data.contactInfo?.email)}
                    type="email"
                    required
                    >Email address</lh-input
                >
            </div>
        `;
    }

    render() {
        return html`
            <div class="wrapper">
                <lh-typography type="heading" selector="h5" size=${this.isMobile ? 'xxl' : 'lg'}>
                    Who needs coverage?
                </lh-typography>
                <lh-typography>
                    You can get a plan for yourself, your spouse, and dependents in your household.
                </lh-typography>
                ${this.data.members.map((_, index) => {
                    return html`<rq-member-card idx=${index}></rq-member-card>`;
                })}
                ${this.minorWithDependentsWarning}
                <lh-button
                    full-width
                    @click=${this.onAddAnotherPerson}
                    icon="plus"
                    icon-position="left"
                    variant="outlined"
                    analytics-id="rate-quote:${this.source}:u65:step2:add-another-person:button-outlined"
                    .disabled=${this.data.members.length >= MAX_MEMBERS ? true : nothing}
                >
                    Add another person
                </lh-button>
                ${this.contactInfo}
                <rq-continue-button
                    analytics-id="rate-quote:${this.source}:u65:step2:continue:button-filled"
                    .disabled=${this.disableContinue}
                ></rq-continue-button>
                <rq-back-button analytics-id="rate-quote:${this.source}:u65:step2:back:button-text"></rq-back-button>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'rq-step-members': RateQuoteMemberStep;
    }
}
